import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { searchProducts } from "../../store/slices/product-slice";
import SellexProductContainer from "../../components/sellexx-product-container";
import { ClipLoader } from "react-spinners";
import { AppDispatch, RootState } from "../../store/store";
import SearchBar from "../../components/search_bar";
import SellexxLogo from "../../components/sellexx-logo";
import CurrencySelector from "../../components/currency-selector";

const SearchResults = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const searchQuery = queryParams.get("q") || "";
  const { items, loading, error } = useSelector(
    (state: RootState) => state.products
  );

  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollPosition, setLastScrollPosition] = useState(0);
  const [selectedCurrency, setSelectedCurrency] = useState("SAR");

  // Header Control
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPosition = window.scrollY;

      if (
        currentScrollPosition > lastScrollPosition &&
        currentScrollPosition > 50
      ) {
        setIsHeaderVisible(false);
      } else {
        setIsHeaderVisible(true);
      }

      setLastScrollPosition(currentScrollPosition);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollPosition]);

  useEffect(() => {
    if (searchQuery) {
      dispatch(searchProducts(searchQuery));
    }
  }, [searchQuery, dispatch]);

  const handleSearch = (query: string) => {
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
    }
  };

  const renderEmptyState = () => (
    <div className="text-center py-16">
      <div className="max-w-md mx-auto">
        <div className="w-24 h-24 mx-auto  rounded-full bg-primary-green/10 flex items-center justify-center">
          <svg
            className="w-12 h-12 text-primary-green"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-3">
          {searchQuery ? "No results found" : "Search"}
        </h3>
        <p className="text-gray-600 mb-6 leading-relaxed">
          {searchQuery
            ? `We couldn't find any socks matching "${searchQuery}". Try different keywords or browse our categories.`
            : "Discover our amazing collection of socks. Use the search bar below to find your perfect pair!"}
        </p>
        <div className="space-y-3"></div>
      </div>
    </div>
  );

  const renderLoadingState = () => (
    <div className="flex flex-col items-center justify-center py-20">
      <ClipLoader size={50} color="#22c55e" />
      <p className="mt-4 text-gray-600 font-medium">Searching for socks...</p>
    </div>
  );

  const renderErrorState = () => (
    <div className="text-center py-16">
      <div className="max-w-md mx-auto">
        <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-red-50 flex items-center justify-center">
          <svg
            className="w-12 h-12 text-red-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L5.314 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-3">
          Something went wrong
        </h3>
        <p className="text-gray-600 mb-6">
          We encountered an error while searching. Please try again or check
          your connection.
        </p>
        <button
          onClick={() => dispatch(searchProducts(searchQuery))}
          className="inline-flex items-center px-6 py-3 bg-primary-green text-white font-medium rounded-lg hover:bg-primary-green/90 transition-all duration-200"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Try Again
        </button>
      </div>
    </div>
  );

  const renderResults = () => (
    <div className="space-y-8">
      {/* Results Header */}
      <div className="flex items-center justify-between border-b border-gray-200 pb-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            Search Results
          </h1>
          <p className="text-gray-600">
            {items.length} {items.length === 1 ? "result" : "results"} found for
            <span className="font-semibold text-primary-green ml-1">
              "{searchQuery}"
            </span>
          </p>
        </div>
        <div className="hidden md:block">
          <span className="text-sm text-gray-500">{items.length} items</span>
        </div>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {items.map((product) => (
          <div
            key={product.id}
            className="transition-all duration-200 hover:-translate-y-1 hover:shadow-lg"
          >
            <SellexProductContainer
              product={product}
              selectedCurrency={selectedCurrency}
            />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Enhanced Header */}
      <header
        className={`sticky top-0 z-50 w-full bg-white/95 backdrop-blur-md border-b border-gray-200/50 shadow-sm transition-all duration-300 ${
          isHeaderVisible ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <SellexxLogo className="h-10 lg:h-14" />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {loading && renderLoadingState()}
        {error && renderErrorState()}
        {!loading && !error && items.length > 0 && renderResults()}
        {!loading && !error && items.length === 0 && renderEmptyState()}
      </main>

      {/* Enhanced Search Bar */}
      <div className="fixed inset-x-0 bottom-6 md:relative md:bottom-auto md:mt-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200/50 p-2 md:p-3">
            <SearchBar
              onSearch={handleSearch}
              className="w-full border-0 shadow-none bg-transparent"
            />
          </div>
        </div>
      </div>

      {/* Bottom Spacing for Mobile */}
      <div className="h-24 md:h-8" />
    </div>
  );
};

export default SearchResults;
